<script setup>
import { ref, onMounted } from 'vue'
import Label from '@/Shared/VisualComponents/Label.vue'
import InputError from '@/Shared/VisualComponents/InputError.vue'
import InputHelpText from '@/Shared/VisualComponents/InputHelpText.vue'

const props = defineProps({
    type: {
        type: String,
    },
    placeholder: {
        type: String,
    },
    name: {
        type: String,
    },
    options: {
        type: Object,
    },
    modelValue: {
        type: [String, Number, Boolean],
    },
    radioValue: {
        type: String,
    },
    errors: {
        type: [String, null],
        default: null,
    },
    autofocus: {
        type: Boolean,
        default: false,
    },
    label: {
        type: String,
    },
    helpText: {
        type: String,
    },
    testid: {
        type: String,
    },
	disabled: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits([
    'update:modelValue',
]);

onMounted(() =>{
    if( props.autofocus ){
        inputRef.value.focus();
    }
})

// a ref to our input - "input" is a bit ambiguous and used by vue a lot, hence "inputRef"
// see https://vuejs.org/guide/essentials/template-refs.html
const inputRef = ref(null)

</script>

<template>
    <div class="block relative mb-3" :data-testid="testid">
        
        <div
            :class="{
                'inline-block': ['radio', 'checkbox'].includes(type),
            }"
        >
            <div 
                class="flex flex-col align-bottom h-full"
                :class="{
                    'float-right': ['radio', 'checkbox'].includes(type),
                }"
            >
                <Label 
                    v-if="label"
                    :target="name"
					:class="{
						'block': !['radio', 'checkbox'].includes(type),
						'flex ml-1 mt-[1px]': ['radio', 'checkbox'].includes(type),
					}"
                >
                    {{label}}
                </Label>
            </div>

            <template v-if="type == 'select'">
                <select
                    :name="name"
                    :id="name"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm': true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @change="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					:disabled="props.disabled"
                >
                    <option v-for="(value, key) in props.options" :key="`select-option-${key}`" :value="key">{{ value }}</option>
                </select>
            </template>

            <template v-else-if="type == 'textarea'">
                <textarea
                    :name="name"
                    :id="name"
                    :placeholder="placeholder"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm': true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					:disabled="props.disabled"
                />
            </template>

            <template v-else-if="type == 'checkbox'">
                <input
                    type="checkbox"
                    :name="name"
                    :id="name"
                    :class="{
                        'flex items-center border border-gray-300 rounded-md shadow-sm p-2 mr-2 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm':true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :checked="props.modelValue ? true : false"
                    @change="$emit('update:modelValue', $event.target.checked)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					:disabled="props.disabled"
                >
            </template>

            <template v-else-if="type == 'password'">
                <input
                    type="password"
                    placeholder="********"
                    :name="name"
                    :id="name"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm':true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					:disabled="props.disabled"
                >
            </template>

            <template v-else-if="type == 'currency'">
                <input
                    type="number"
					step="0.01"
                    placeholder="$100.00"
                    :name="name"
                    :id="name"
                    :class="{
                        'text-right mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm':true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					:disabled="props.disabled"
                >
            </template>

            <template v-else-if="type == 'date'">
                <input
                    type="date"
                    placeholder="DD-MM-YY"
                    :name="name"
                    :id="name"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm':true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					max="9999-12-31"
					:disabled="props.disabled"
                >
            </template>

			<template v-else-if="type == 'email'">
                <input
                    :placeholder="placeholder"
                    :name="name"
                    :id="name"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm': true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					type="email"
					:disabled="props.disabled"
                >
            </template>

			<template v-else-if="type == 'website'">
                <input
                    :placeholder="placeholder"
                    :name="name"
                    :id="name"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm': true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					type="text"
					:disabled="props.disabled"
                >
            </template>

			<template v-else-if="type == 'phone'">
                <input
                    :placeholder="placeholder"
                    :name="name"
                    :id="name"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm': true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					type="tel"
					v-maska="['#### ### ###', '#### ####', '##############']"
					:disabled="props.disabled"
                >
            </template>

			<template v-else-if="type == 'post_code'">
                <input
                    :placeholder="placeholder"
                    :name="name"
                    :id="name"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm': true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					v-maska="'####'"
					:disabled="props.disabled"
                >
            </template>

				<template v-else-if="type == 'state'">
                <input
                    :placeholder="placeholder"
                    :name="name"
                    :id="name"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm': true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					v-maska="['AAA', 'AAAA']"
					:disabled="props.disabled"
                >
            </template>

			<template v-else-if="type == 'abn'">
                <input
                    :placeholder="placeholder"
                    :name="name"
                    :id="name"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm': true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					v-maska="'## ### ### ###'"
					:disabled="props.disabled"
                >
            </template>

			<template v-else-if="type == 'xero_id'">
                <input
                    :placeholder="placeholder"
                    :name="name"
                    :id="name"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm': true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					:disabled="props.disabled"
                >
            </template>

			<template v-else-if="type == 'card'">
                <input
                    :placeholder="placeholder"
                    :name="name"
                    :id="name"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm': true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					v-maska="'#### #### #### ####'"
					:disabled="props.disabled"
                >
            </template>

            <template v-else>
                <input
					v-bind="$attrs"
                    :placeholder="placeholder"
					:type="type"
                    :name="name"
                    :id="name"
                    :class="{
                        'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm': true,
                        'border-slate-300': errors===null,
                        'border-red-600 ring-2 ring-red-600': errors!==null,
                    }"
                    :value="props.modelValue"
                    @input="$emit('update:modelValue', $event.target.value)"
                    ref="inputRef"
                    :data-testid="testid + '_input'"
					:disabled="props.disabled"
                >
            </template>
			<InputHelpText :helpText="helpText" />
            
        </div>
        <InputError :error="errors" />
        
    </div>
</template>


<style>
input:Error {
	border-color: red;
}

input:disabled, select:disabled, textarea:disabled {
	background: rgba(239, 239, 239, 0.3);
}
</style>